import { Component, Input, Output, EventEmitter, signal, computed, input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SocketService, Player } from '../../../../services/socket';

export interface CheatSheetPlayer extends Player {
  standardAuctionValue: number | null;
  standardRank: number | null;
  pprAuctionValue: number | null;
  pprRank: number | null;
  positionalRank: number | null;
}

type PositionFilter = 'ALL' | 'QB' | 'RB' | 'WR' | 'TE' | 'K' | 'D';
type RankingMode = 'standard' | 'ppr';
type ViewMode = 'all' | 'watchlist';

interface RankingOption {
  value: RankingMode;
  label: string;
}

@Component({
  selector: 'app-cheat-sheet',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './cheat-sheet.html',
  styleUrl: './cheat-sheet.css'
})
export class CheatSheet implements OnInit, OnDestroy {
  @Input() leagueId!: number;
  @Input() isOpen = false;
  @Input() teamBudget: number = 200;
  @Output() playerSelected = new EventEmitter<Player>();
  @Output() watchlistToggled = new EventEmitter<{ playerId: number, add: boolean }>();

  watchlistPlayerIds = input<Set<number>>(new Set());

  readonly positions: PositionFilter[] = ['ALL', 'QB', 'RB', 'WR', 'TE', 'K', 'D'];
  readonly rankingOptions: RankingOption[] = [
    { value: 'standard', label: 'Standard' },
    { value: 'ppr', label: 'PPR' },
  ];

  allPlayers = signal<CheatSheetPlayer[]>([]);
  isLoading = signal(false);
  searchQuery = signal('');
  activePosition = signal<PositionFilter>('ALL');
  rankingMode = signal<RankingMode>('standard');
  viewMode = signal<ViewMode>('all');

  private refreshInterval: ReturnType<typeof setInterval> | null = null;

  constructor(public socketService: SocketService) {}

  // Set of drafted player IDs for quick lookup
  private draftedPlayerIds = computed(() => {
    const settings = this.socketService.draftSettings();
    const history = settings?.draftHistory || [];
    return new Set(history.map(entry => entry.player_id));
  });

  // Count of available watchlisted players (for badge)
  watchlistCount = computed(() => {
    const draftedIds = this.draftedPlayerIds();
    const watchlist = this.watchlistPlayerIds();
    return this.allPlayers().filter(p => !draftedIds.has(p.id) && watchlist.has(p.id)).length;
  });

  // Filter players: exclude drafted, apply view/position/search filters, sort by ranking mode
  filteredPlayers = computed(() => {
    const players = this.allPlayers();
    const draftedIds = this.draftedPlayerIds();
    const position = this.activePosition();
    const query = this.searchQuery().toLowerCase().trim();
    const mode = this.rankingMode();
    const viewMode = this.viewMode();
    const watchlist = this.watchlistPlayerIds();

    let filtered = players.filter(p => !draftedIds.has(p.id));

    if (viewMode === 'watchlist') {
      filtered = filtered.filter(p => watchlist.has(p.id));
    }

    if (position !== 'ALL') {
      filtered = filtered.filter(p => this.normalizePosition(p.position) === position);
    }

    if (query) {
      filtered = filtered.filter(p =>
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(query) ||
        p.team.toLowerCase().includes(query)
      );
    }

    // Sort by selected ranking mode
    filtered = [...filtered].sort((a, b) => {
      const rankA = mode === 'ppr' ? (a.pprRank ?? 9999) : (a.standardRank ?? 9999);
      const rankB = mode === 'ppr' ? (b.pprRank ?? 9999) : (b.standardRank ?? 9999);
      return rankA - rankB;
    });

    return filtered;
  });

  // Count available players per position
  positionCounts = computed(() => {
    const players = this.allPlayers();
    const draftedIds = this.draftedPlayerIds();
    const available = players.filter(p => !draftedIds.has(p.id));

    const counts: Record<string, number> = { ALL: available.length };
    for (const pos of ['QB', 'RB', 'WR', 'TE', 'K', 'D']) {
      counts[pos] = available.filter(p => this.normalizePosition(p.position) === pos).length;
    }
    return counts;
  });

  ngOnInit() {
    this.loadAllPlayers();

    // Refresh player list periodically to stay in sync
    this.refreshInterval = setInterval(() => {
      if (this.isOpen) {
        this.loadAllPlayers();
      }
    }, 30000);
  }

  ngOnDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  async loadAllPlayers() {
    if (!this.leagueId) return;

    this.isLoading.set(true);
    try {
      const response = await fetch(
        `/api/leagues/${this.leagueId}/cheat-sheet`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const players = await response.json();
        this.allPlayers.set(players);
      }
    } catch (error) {
      console.error('Error loading cheat sheet:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  setPosition(position: PositionFilter) {
    this.activePosition.set(position);
  }

  setViewMode(mode: ViewMode) {
    this.viewMode.set(mode);
  }

  toggleWatchlist(event: MouseEvent, player: CheatSheetPlayer) {
    event.stopPropagation();
    const add = !this.watchlistPlayerIds().has(player.id);
    this.watchlistToggled.emit({ playerId: player.id, add });
  }

  onSearch(query: string) {
    this.searchQuery.set(query);
  }

  onRankingModeChange(mode: string) {
    this.rankingMode.set(mode as RankingMode);
  }

  getPlayerRank(player: CheatSheetPlayer): number | null {
    return this.rankingMode() === 'ppr' ? player.pprRank : player.standardRank;
  }

  getPlayerValue(player: CheatSheetPlayer): number | null {
    const baseValue = this.rankingMode() === 'ppr' ? player.pprAuctionValue : player.standardAuctionValue;
    if (baseValue == null) return null;
    const scale = this.teamBudget / 200;
    return Math.round(baseValue * scale);
  }

  selectPlayer(player: CheatSheetPlayer) {
    this.playerSelected.emit(player);
  }

  onPhotoError(event: Event, player: CheatSheetPlayer) {
    (event.target as HTMLImageElement).style.display = 'none';
    const container = (event.target as HTMLImageElement).parentElement;
    if (container) {
      const initials = document.createElement('span');
      initials.className = 'player-photo-initials';
      initials.textContent = `${player.first_name[0]}${player.last_name[0]}`;
      container.appendChild(initials);
    }
  }

  normalizePosition(position: string): string {
    const pos = position.toUpperCase();
    if (pos === 'DEF' || pos === 'DST') return 'D';
    if (pos === 'PK') return 'K';
    return pos;
  }

  trackByPlayerId(index: number, player: CheatSheetPlayer): number {
    return player.id;
  }
}
